import { NavigationHelperService } from '@shared/helpers/navigation.helpers.service';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IonSearchbar, ModalController, ViewDidEnter } from '@ionic/angular';
import { SearchAnalyticsService } from 'src/app/search/services/search-analytics.service';
import { ProductStateService, SEARCH_TYPE } from '@usf/ngrx-product';
import { PlatformService } from '@panamax/app-state';
import {
  Typeahead,
  TypeaheadGroup,
  TypeaheadGroupNames,
  TypeaheadItem,
} from '@usf/product-types';
import { Subscription } from 'rxjs';
import { SEARCH_PAGES_ENUM } from '@shared/constants/search-pages.enum';
import { filter } from 'rxjs/operators';
import { NavigationStart, Router } from '@angular/router';
import { TYPEAHEAD_DEBOUNCE_MS } from '@shared/constants/typeahead-constants';
import { FEATURES } from '@shared/constants/splitio-features';
import { ToggleNewSearchService } from '@shared/services/toggle-new-search/toggle-new-search.service';
import { TypeaheadService } from '@shared/services/typeahead/typeahead.service';
import { PATHS } from '@shared/constants/paths';
import { CoveoService } from '@shared/services/coveo/coveo.service';
import { QrCodeScannerComponent } from '@shared/components/qr-code-scanner/qr-code-scanner.component';
import { ToastService } from '@shared/services/toast/toast.service';
import { MessageTypeEnum } from 'src/app/ngrx-message/constants/messageTypeEnum';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-search-modal',
  templateUrl: './search-modal.component.html',
  styleUrls: ['./search-modal.component.scss'],
})
export class SearchModalComponent implements ViewDidEnter, OnInit, OnDestroy {
  @ViewChild(IonSearchbar, { static: false }) searchbar: IonSearchbar;
  @ViewChild('qrScanner') qrScanner: QrCodeScannerComponent;
  @Input() mode: string;
  typeaheadVisible = false;
  typeaheadNoResults = false;
  typeahead: Typeahead;
  modalTypeahead$: Subscription;
  typeaheadDebounce: number = TYPEAHEAD_DEBOUNCE_MS;
  minLength = 2;
  activeIndex = -1;
  customTypeAheadGroup: TypeaheadGroup[] = [];
  routeEvents$: Subscription;
  toogleNewSearchFeatureFlag = FEATURES.split_user_toggle_new_search.name;
  scanQrCodeFeatureFlag = FEATURES.split_dynamic_scan_qr_code.name;
  isNewSearchEnabled = false;
  isNewSearchEnabledSubscription$: Subscription;
  isTyping = false;

  constructor(
    private modalController: ModalController,
    readonly platformService: PlatformService,
    private searchAnalyticsService: SearchAnalyticsService,
    private navigationHelperService: NavigationHelperService,
    private productStateService: ProductStateService,
    private readonly toggleNewSearchService: ToggleNewSearchService,
    public router: Router,
    readonly typeaheadService: TypeaheadService,
    private coveoService: CoveoService,
    private toastService: ToastService,
    private translateService: TranslateService,
  ) {}

  ionViewDidEnter(): void {
    // doesn't work without timeout, ref: https://github.com/ionic-team/ionic-framework/issues/17745
    setTimeout(() => this.searchbar.setFocus(), 500);
  }

  ngOnInit(): void {
    this.routeEvents$ = this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(ev => {
        this.clearInputText();
        this.cancelTypeahead();
      });

    this.isNewSearchEnabledSubscription$ = this.toggleNewSearchService
      .isNewSearchEnabled()
      .subscribe(newSearchEnabled => {
        this.isNewSearchEnabled = newSearchEnabled;
      });
  }

  ngOnDestroy(): void {
    this.routeEvents$?.unsubscribe();
    this.modalTypeahead$?.unsubscribe();
    this.isNewSearchEnabledSubscription$?.unsubscribe();
  }

  async executeSearch($event: any) {
    const searchText = $event?.target?.value?.trim();
    this.searchAnalyticsService.trackSearchSubmit(
      { searchText },
      SEARCH_TYPE.catalogSearch,
      this.isNewSearchEnabled,
    );
    await this.closeModal();

    if (this.isNewSearchEnabled) {
      this.coveoService.clearCustomContext();
      this.typeaheadService.submitSearch();
      this.router
        .navigate([`/${PATHS.NEW_SEARCH}`], { queryParams: { searchText } })
        .then(() => {
          this.typeaheadService.searchNavigationCompleted();
        });
    } else {
      const queryParams = {
        searchText: searchText,
        ...(!!this.mode && { mode: this.mode }),
      };
      this.navigationHelperService.routeToSearch(queryParams);
    }
  }

  handleInputChange(evt) {
    const inputValue = evt?.target?.value?.trim();
    if (inputValue.length > this.minLength) {
      if (this.isNewSearchEnabled) {
        this.typeaheadVisible = true;
        this.typeaheadService.searchBoxController.updateText(inputValue);
        this.typeaheadService.instantProductsController.updateQuery(inputValue);
        this.typeaheadService.fieldSuggestionController.updateQuery(inputValue);
      } else {
        this.modalTypeahead$?.unsubscribe();
        this.modalTypeahead$ = this.productStateService
          .getTypeahead(inputValue)
          .subscribe(result => {
            this.typeahead = this.filterGroupsWithoutItems(result);
            this.typeaheadVisible = true;
            this.typeaheadNoResults = !result?.groups?.some(
              g => g.items?.length > 0,
            );
            this.activeIndex = -1;
            this.highlightActiveIndex();
          });
      }
    } else {
      this.typeaheadVisible = false;
      this.customTypeAheadGroup = [];
    }
  }

  filterGroupsWithoutItems(typeahead: Typeahead): Typeahead {
    if (!typeahead) return typeahead;
    return {
      ...typeahead,
      groups: typeahead.groups?.filter(group => group.items?.length > 0),
    };
  }

  highlightActiveIndex() {
    let index = 0;
    this.customTypeAheadGroup = this.typeahead.groups.map(group => {
      const typeAheadGroup = { ...group } as TypeaheadGroup;
      typeAheadGroup.items = typeAheadGroup.items.map(item => {
        const typeAheadItem = { ...item, isActive: false };
        if (index === this.activeIndex) {
          typeAheadItem.isActive = true;
        }
        index++;
        return typeAheadItem;
      });
      return typeAheadGroup;
    });
  }

  async handleItemClick(item: TypeaheadItem, category: string, rank: number) {
    if (!item || !item.searchDimensionId) {
      return;
    }
    if (category === TypeaheadGroupNames.products) {
      this.searchByProductNumber(item.searchDimensionId, rank);
    } else {
      // For Categories & Brands
      this.searchByDimensionId(item.searchDimensionId);
    }
    await this.closeModal();
  }

  searchByProductNumber(productNumber: number, rank: number) {
    const queryParams = {
      lnksrc: SEARCH_PAGES_ENUM.productTypeAhead,
      rank,
    };
    this.navigationHelperService.routeToProductDetailsQueryParams(
      productNumber,
      queryParams,
    );
    this.typeaheadVisible = false;
  }

  searchByDimensionId(dimensionId: number) {
    this.navigationHelperService.routeToSearch({
      searchFilterProperties: dimensionId,
      originSearchPage: SEARCH_PAGES_ENUM.typeAhead,
    });
    this.typeaheadVisible = false;
  }

  clearInputText() {
    if (!!this.searchbar) {
      this.searchbar.value = '';
    }
  }

  cancelTypeahead() {
    this.modalTypeahead$?.unsubscribe();
    this.typeaheadVisible = false;
  }

  async closeModal() {
    if (this.qrScanner?.isScanning) {
      this.qrScanner.stopScan();
    }
    await this.modalController.dismiss();
  }

  async onTypeaheadVisible(event: boolean) {
    this.typeaheadVisible = event;
    await this.modalController.dismiss();
  }

  private async requestCameraPermission(): Promise<string> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      stream.getTracks().forEach(track => track.stop());
      return '';
    } catch (err: any) {
      let errorMessage = 'i18n.qrCodeScanner.error.cameraAccessError';
      if (err?.name === 'NotAllowedError') {
        errorMessage = 'i18n.qrCodeScanner.error.cameraPermissionDenied';
      } else if (err?.name === 'NotFoundError') {
        errorMessage = 'i18n.qrCodeScanner.error.cameraNotFound';
      }
      return errorMessage;
    }
  }

  async openQrCodeScanner() {
    const error = await this.requestCameraPermission();

    if (error !== '') {
      this.handleQrCodeScanError(error);
      return;
    }

    this.searchbar?.getInputElement().then(input => input.blur());
    this.qrScanner?.openQrCodeScanner();
  }

  setIsTyping(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.isTyping = !!input?.value?.trim();
  }

  handleQrCodeScanComplete() {
    this.closeModal();
  }

  handleQrCodeScanError(errorMessage: string) {
    if (this.qrScanner?.isScanning) {
      this.qrScanner.stopScan();
    }

    this.closeModal();

    this.toastService.presentToastMsg(
      this.translateService.instant(errorMessage),
      'red-toast',
      MessageTypeEnum.error,
      [],
    );
  }
}
